import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';

export interface CompanyData {
  id: string;
  name: string;
  slug: string;
  doc_cnpj?: string;
  razao_social?: string;
  nome_fantasia?: string;
  status: string;
  timezone: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_estado?: string;
  endereco_cep?: string;
  telefone?: string;
  email?: string;
  branding?: any;
}

export function useCompanyData(companyId?: string) {
  const { activeCompany } = useAuth();
  const targetCompanyId = companyId || activeCompany?.id;

  return useQuery({
    queryKey: ['company-data', targetCompanyId],
    queryFn: async (): Promise<CompanyData | null> => {
      if (!targetCompanyId) return null;

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', targetCompanyId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!targetCompanyId,
  });
}

export function useUpdateCompanyData() {
  const queryClient = useQueryClient();
  const { activeCompany } = useAuth();

  return useMutation({
    mutationFn: async (data: Partial<CompanyData>) => {
      if (!activeCompany?.id) throw new Error('No active company');

      const { error } = await supabase
        .from('companies')
        .update(data)
        .eq('id', activeCompany.id);

      if (error) throw error;
    },
    onSuccess: () => {
      // Invalidate all company-data related queries
      queryClient.invalidateQueries({ queryKey: ['company-data'] });
      queryClient.invalidateQueries({ queryKey: ['user-companies'] });
      // Force immediate refetch of current company data
      if (activeCompany?.id) {
        queryClient.refetchQueries({ queryKey: ['company-data', activeCompany.id] });
      }
    },
  });
}